import React from 'react';
import styles from './LeadCaptureForm.scss';
import { ISendersData, MessageEntry } from '@wix/chat-web';
import { Constants, Message } from '@wix/chat-sdk';
import { I18nextProvider } from 'react-i18next';
import ThankYouScreen from './ThankYouScreen';
import Form from './Form';
import { connect } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import { messagesFontFamily, theme, Theme } from '../../reducers';
import {
  updateAllowInput,
  updateWasLeadCaptureFormFilled,
} from '../../actions';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { wixAnalytics } from '../../services/wixAnalytics';
import { EditorViewMode } from '../../constants/editor-view-mode';
import * as Sentry from '@sentry/browser';
import { WidgetSenderIdentityType } from '../../constants/widget-sender-identity';
import TitleBubble from '../TitleBubble';
import classNames from 'classnames';

const MessagePositionInGroup = Constants.MessagePositionInGroup;

interface LeadCaptureFormProps extends InjectedExperimentsProps {
  groupPosition: string;
  introText?: string;
  fields: string;
  thankYouMessage: string;
  i18nData: any;
  themeData: Theme;
  fontFamily: string;
  updateAllowInput(boolean): any;
  wasLeadCaptureFormFilled: boolean;
  updateWasLeadCaptureFormFilled(boolean): void;
  viewMode: string;
  widgetSenderIdentity: string;
  widgetSenderData: ISendersData;
  message: Message;
}

interface LeadCaptureFormState {}

class LeadCaptureForm extends React.Component<
  LeadCaptureFormProps,
  LeadCaptureFormState
> {
  getGroupPositionForIntro = (outerPosition) => {
    switch (outerPosition) {
      case MessagePositionInGroup.SINGLE: {
        return MessagePositionInGroup.START;
      }
      case MessagePositionInGroup.END: {
        return MessagePositionInGroup.MIDDLE;
      }
      default: {
        return outerPosition;
      }
    }
  };

  getGroupPositionForForm = (outerPosition, hasIntroBefore) => {
    if (!hasIntroBefore) {
      return outerPosition;
    }

    switch (outerPosition) {
      case MessagePositionInGroup.SINGLE: {
        return MessagePositionInGroup.END;
      }
      case MessagePositionInGroup.START: {
        return MessagePositionInGroup.MIDDLE;
      }
      default: {
        return outerPosition;
      }
    }
  };

  getTitle = () => {
    const { widgetSenderData, widgetSenderIdentity, message } = this.props;

    const isSenderIdentityIncludeTitle =
      widgetSenderIdentity === WidgetSenderIdentityType.BUSINESS ||
      widgetSenderIdentity === WidgetSenderIdentityType.AGENT;
    const isGroupPositionIncludeTitle =
      message?.groupPosition === Constants.MessagePositionInGroup.SINGLE ||
      message?.groupPosition === Constants.MessagePositionInGroup.START;
    const isTitleShouldBeDisplay =
      isSenderIdentityIncludeTitle && isGroupPositionIncludeTitle;

    const titleName =
      widgetSenderIdentity === WidgetSenderIdentityType.BUSINESS &&
      !_isEmpty(widgetSenderData)
        ? widgetSenderData[0].userName
        : message?.senderDisplayData?.name;

    return isTitleShouldBeDisplay ? titleName : undefined;
  };

  getIntroText = () => {
    const { introText, groupPosition, widgetSenderIdentity } = this.props;
    const narrowStyle =
      widgetSenderIdentity !== WidgetSenderIdentityType.ANONYMOUS;

    return (
      introText && (
        <div className={styles.entry} data-hook={'lcf-intro-message'}>
          <MessageEntry
            groupPosition={this.getGroupPositionForIntro(groupPosition)}
            position={Constants.MessageDirections.Incoming}
            title={this.getTitle()}
            separateTitle={false}
          >
            <div
              className={classNames(styles.introText, {
                [styles.narrowStyle]: narrowStyle,
              })}
            >
              {introText}
            </div>
          </MessageEntry>
        </div>
      )
    );
  };

  trackEvent = () => {
    wixAnalytics.lead();
  };

  handleSubmit = () => {
    this.props.updateAllowInput(true);
    this.props.updateWasLeadCaptureFormFilled(true);
    this.trackEvent();
  };

  componentDidMount(): void {
    Sentry.addBreadcrumb({ category: 'LeadCaptureForm' });
  }

  render() {
    const {
      groupPosition,
      introText,
      thankYouMessage,
      i18nData,
      themeData,
      fields,
      fontFamily,
      wasLeadCaptureFormFilled,
      viewMode,
      widgetSenderIdentity,
      message,
    } = this.props;
    const formGroupPosition = this.getGroupPositionForForm(
      groupPosition,
      !!introText,
    );
    const fieldsParsed = JSON.parse(fields);
    const { backgroundColor, color } = themeData.button.enable;
    const borderRadius = themeData.button.borderRadius || 20;
    const narrowStyle =
      widgetSenderIdentity !== WidgetSenderIdentityType.ANONYMOUS;

    return (
      <I18nextProvider i18n={i18nData}>
        <div
          className={styles.leadCaptureForm}
          data-hook="lead-capture-form"
          style={{ fontFamily }}
        >
          {this.getIntroText()}
          {wasLeadCaptureFormFilled ? (
            <ThankYouScreen
              thankYouMessage={thankYouMessage}
              groupPosition={formGroupPosition}
              narrowStyle={narrowStyle}
              title={this.getTitle()}
            />
          ) : (
            <div>
              {!introText && <TitleBubble message={message} />}
              <Form
                key={fields}
                fields={fieldsParsed}
                groupPosition={formGroupPosition}
                onSubmit={this.handleSubmit}
                backgroundColor={backgroundColor}
                color={color}
                borderRadius={borderRadius}
                withAutoFocus={viewMode !== EditorViewMode.EDITOR}
                narrowStyle={narrowStyle}
              />
            </div>
          )}
        </div>
      </I18nextProvider>
    );
  }
}

function mapStateToProps(state) {
  return {
    fontFamily: messagesFontFamily(state),
    i18nData: state.i18nData,
    themeData: theme(state),
    wasLeadCaptureFormFilled: state.wasLeadCaptureFormFilled,
    viewMode: state.viewMode,
    widgetSenderIdentity: state.widgetSenderIdentity,
    widgetSenderData: state.widgetSenderData,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    updateAllowInput: (allowInput) => dispatch(updateAllowInput(allowInput)),
    updateWasLeadCaptureFormFilled: (wasLeadCaptureFormFilled) =>
      dispatch(updateWasLeadCaptureFormFilled(wasLeadCaptureFormFilled)),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withExperiments(LeadCaptureForm));
